import * as React from "react"
import { Link } from "react-router-dom"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { FastImageFallback } from "@/components/FastImageFallback"
import { ShareDropdown } from "@/components/marketing/ShareDropdown"
import { Calendar, MapPin, Users, ArrowRight, Globe } from "lucide-react"
import { cn, parseLocalDate } from "@/lib/utils"
import type { EventType } from "@/utils/eventUtils"
import { useLanguage } from "@/hooks/useLanguage"

interface FestCardProps {
  slug: string
  title: string
  titleEn?: string
  description: string
  descriptionEn?: string
  location: string
  locationEn?: string
  startDate: string
  endDate: string
  imageUrl: string
  attendeeCount?: number
  ticketPrice?: string
  status: "upcoming" | "ongoing" | "past" | "coming_soon"
  featured?: boolean
  className?: string
  isExternal?: boolean
  externalLink?: string | null
  theme?: string
  website?: string | null
  eventType?: EventType
}

const FestCard = ({
  slug,
  title,
  titleEn,
  description,
  descriptionEn,
  location,
  locationEn,
  startDate,
  endDate,
  imageUrl,
  attendeeCount,
  ticketPrice,
  status,
  featured = false,
  className,
  isExternal,
  externalLink,
  theme,
  website,
  eventType = 'nomad-fest'
}: FestCardProps) => {
  const { language } = useLanguage();
  
  // Get localized content with fallback to original
  const localizedTitle = language === 'en' && titleEn ? titleEn : title;
  const localizedDescription = language === 'en' && descriptionEn ? descriptionEn : description;
  const localizedLocation = language === 'en' && locationEn ? locationEn : location;
  // Parse early bird pricing if present
  const parsedPrice = React.useMemo(() => {
    const val = ticketPrice?.toString().trim();
    if (!val) return null;
    if (val.toLowerCase() === 'free') return { type: 'free' };
    
    // Check for early bird pattern: "Early Bird: XX% off until DD/MM"
    const earlyBirdMatch = val.match(/(.+?):\s*(\d+)%\s*off\s*until\s*(.+)/i);
    if (earlyBirdMatch) {
      return {
        type: 'early-bird',
        label: earlyBirdMatch[1].trim(),
        discount: earlyBirdMatch[2],
        endDate: earlyBirdMatch[3].trim(),
        rawPrice: val
      };
    }
    
    return { type: 'regular', price: val };
  }, [ticketPrice]);
  const formatDate = (date: string) => {
    return (parseLocalDate(date) ?? new Date(date)).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const getStatusBadge = () => {
    switch (status) {
      case "upcoming":
        return <Badge variant="secondary" className="bg-primary/20 text-primary">Upcoming</Badge>
      case "ongoing":
        return <Badge variant="default" className="bg-green-500/20 text-green-600">Live Now</Badge>
      case "past":
        return <Badge variant="outline" className="text-muted-foreground">Past Event</Badge>
      case "coming_soon":
        return <Badge variant="secondary" className="bg-amber-500/20 text-amber-600">Coming Soon</Badge>
    }
  }

  const getThemeLabel = () => {
    switch (theme) {
      case 'nomad-fest': return 'Nomad Fest'
      case 'snomad-fest': return 'Snomad Fest'
      case 'ai-fest': return 'AI Festival'
      case 'longevity-fest': return 'Longevity Festival'
      case 'fire-fest': return 'Fire Fest'
      case 'impact-fest': return 'Impact Fest'
      case 'bootcamp': return 'Bootcamp'
      case 'ai-bootcamp': return 'AI Bootcamp'
      case 'academy': return 'Academy'
      case 'accelerator': return 'Accelerator'
      case 'residency': return 'Residency'
      case 'coliving': return 'Coliving'
      case 'family-hostel': return 'Family Hostel'
      case 'workation': return 'Workation'
      case 'retreat': return 'Retreat'
      case 'award': return 'Award'
      default: return null
    }
  }

  const getThemeColor = () => {
    switch (theme) {
      case 'nomad-fest': return 'bg-blue-500/90 text-white'
      case 'snomad-fest': return 'bg-sky-400/90 text-white'
      case 'ai-fest': return 'bg-purple-500/90 text-white'
      case 'longevity-fest': return 'bg-green-500/90 text-white'
      case 'fire-fest': return 'bg-red-500/90 text-white'
      case 'impact-fest': return 'bg-cyan-500/90 text-white'
      case 'bootcamp': return 'bg-orange-500/90 text-white'
      case 'ai-bootcamp': return 'bg-violet-500/90 text-white'
      case 'academy': return 'bg-indigo-500/90 text-white'
      case 'accelerator': return 'bg-rose-500/90 text-white'
      case 'residency': return 'bg-fuchsia-500/90 text-white'
      case 'coliving': return 'bg-teal-500/90 text-white'
      case 'family-hostel': return 'bg-lime-500/90 text-white'
      case 'workation': return 'bg-amber-500/90 text-white'
      case 'retreat': return 'bg-emerald-500/90 text-white'
      case 'award': return 'bg-yellow-500/90 text-white'
      default: return 'bg-primary/90 text-primary-foreground'
    }
  }

  return (
    <Card className={cn(
      "group overflow-hidden transition-all duration-300 hover:shadow-festival hover:-translate-y-2",
      featured && "ring-2 ring-primary ring-offset-4 shadow-festival",
      className
    )}>
      <div className="relative">
        {/* Clickable Image - Only use external link if isExternal is true */}
        {status === "coming_soon" ? (
          <div className="block h-48 overflow-hidden">
            <FastImageFallback 
              src={imageUrl}
              alt={localizedTitle}
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
              priority={featured}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          </div>
        ) : isExternal && externalLink ? (
          <a href={externalLink} target="_blank" rel="noopener noreferrer" className="block h-48 overflow-hidden">
            <FastImageFallback 
              src={imageUrl}
              alt={localizedTitle}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
              decoding="async"
              priority={featured}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          </a>
        ) : (
          <Link to={`/events/festivals/${slug}`} className="block h-48 overflow-hidden">
            <FastImageFallback 
              src={imageUrl}
              alt={localizedTitle}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
              decoding="async"
              priority={featured}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          </Link>
        )}
        
        {/* Status Badge */}
        <div className="absolute top-4 left-4">
          {getStatusBadge()}
        </div>
        
        {/* Category Badge */}
        {getThemeLabel() && (
          <div className="absolute top-4 right-4">
            <Badge className={cn("shadow-lg", getThemeColor())}>
              {getThemeLabel()}
            </Badge>
          </div>
        )}
        
        {/* Featured Badge */}
        {featured && (
          <div className="absolute bottom-4 right-4">
            <Badge className="bg-gradient-accent text-white shadow-lg">Featured</Badge>
          </div>
        )}
      </div>
      
      <CardContent className="p-6">
        {/* Title */}
        <h3 className="text-xl font-bold mb-2 line-clamp-2 group-hover:text-primary transition-colors">
          {localizedTitle}
        </h3>
        
        {/* Description */}
        <p className="text-muted-foreground mb-4 line-clamp-2">
          {localizedDescription}
        </p>
        
        {/* Meta Info - Order: Location, Date, Participants */}
        <div className="space-y-2 mb-6">
          <div className="flex items-center text-sm text-muted-foreground">
            <MapPin className="h-4 w-4 mr-2" />
            {localizedLocation}
          </div>
          
          <div className="flex items-center text-sm text-muted-foreground">
            <Calendar className="h-4 w-4 mr-2" />
            {formatDate(startDate)} - {formatDate(endDate)}
          </div>
          
          {attendeeCount !== undefined && attendeeCount > 0 && (
            <div className="flex items-center text-sm text-muted-foreground">
              <Users className="h-4 w-4 mr-2" />
              {attendeeCount} attendees
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="space-y-3">
          {parsedPrice && (() => {
            const ticketHref = isExternal && externalLink ? externalLink : `/events/festivals/${slug}/shop`;
            const priceInner = parsedPrice.type === 'free' ? (
              <div className="text-lg font-bold text-primary hover:underline">Get Tickets</div>
            ) : parsedPrice.type === 'early-bird' ? (
              <div className="space-y-1">
                <Badge variant="secondary" className="bg-emerald-500/10 text-emerald-600 text-xs px-2 py-0.5">
                  {parsedPrice.label} • {parsedPrice.discount}% off
                </Badge>
                <div className="text-sm text-muted-foreground">Until {parsedPrice.endDate}</div>
              </div>
            ) : (
              <div className="text-lg font-bold text-primary truncate hover:underline">
                {parsedPrice.price}
              </div>
            );
            // Coming-soon events have no shop yet — keep the price non-clickable.
            if (status === 'coming_soon') {
              return <div className="flex-1 min-w-0">{priceInner}</div>;
            }
            return isExternal && externalLink ? (
              <a href={ticketHref} target="_blank" rel="noopener noreferrer" className="flex-1 min-w-0 block">{priceInner}</a>
            ) : (
              <Link to={ticketHref} className="flex-1 min-w-0 block">{priceInner}</Link>
            );
          })()}
          
          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            {website && (
              <Button variant="outline" size="sm" asChild>
                <a href={website} target="_blank" rel="noopener noreferrer">
                  <Globe className="h-4 w-4 mr-1" />
                  Website
                </a>
              </Button>
            )}
            
            <ShareDropdown
              eventId={slug}
              eventType={eventType}
              eventName={localizedTitle}
              location={localizedLocation}
              startDate={formatDate(startDate)}
              endDate={formatDate(endDate)}
              description={localizedDescription}
              variant="outline"
              size="sm"
            />
          </div>
          
          {/* Learn More Button - Only use external link if isExternal is true */}
          {status === "coming_soon" ? (
            <Button className="w-full" asChild>
              <Link to="/contact">
                Interested? Get in Touch
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          ) : isExternal && externalLink ? (
            <Button className="w-full" asChild>
              <a href={externalLink} target="_blank" rel="noopener noreferrer">
                Learn More
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
          ) : (
            <Button className="w-full" asChild>
              <Link to={`/events/festivals/${slug}`}>
                Learn More
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

export { FestCard }